import { useState, useRef, useEffect } from 'react';
import ArrowIcon from '@/public/icons/select-arrow.svg';

type Option = {
  label: string;
  value: string;
};

type MultiSelectProps = {
  options: Option[];
  placeholder?: string;
  icon?: React.ReactNode;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
};

const MultiSelect = ({
  options,
  placeholder,
  icon,
  selected,
  setSelected,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    setSelected(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value],
    );
  };

  const selectedOptions = selected.length
    ? options
        .filter(o => selected.includes(o.value))
        .map(o => o.label)
        .join(', ')
    : null;

  return (
    <div className="multi-select relative w-full" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`multi-select__inner-wrapper ${
          selectedOptions ? 'active' : ''
        } flex cursor-pointer items-center bg-white
            p-2 `}
      >
        <div className="flex items-center">
          {icon && (
            <span className="multi-select__label-icon mr-[6px] h-5 w-5 lg:h-[18px] lg:w-[18px]">
              {icon}
            </span>
          )}
          <span className={`multi-select__label-text truncate`}>
            {selectedOptions ?? placeholder}
          </span>
        </div>
        <div className="flex items-center">
          <span
            className={`multi-select__arrow-icon h-5 w-5 transition-all duration-300 lg:h-[18px] lg:w-[18px] ${
              isOpen ? 'open' : ''
            }`}
          >
            <ArrowIcon />{' '}
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="multi-select__dropdown absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {options.map(opt => (
            <label
              key={opt.value}
              className={`${
                selected.includes(opt.value) ? 'checked' : ''
              } flex cursor-pointer items-center justify-between gap-2 px-[22px] py-[14px] hover:bg-gray-100`}
            >
              {opt.label}
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
